import { keepTwoOrLine, keepOneAndWan, keepZeroOrLine } from '@/utils/utils';
import circle_spin from '../../imgs/circle_spin.png';

// 类型一：旋转背景图 + 自定义图例 + 中间值和标签
export const getPieChartOptionWithSpin = (
  pieLegendData?: PieLegendItem[],
  unit?: string,
  centerLabel?: string,
  integerValue?: boolean
) => {
  // 计算总值
  const total = pieLegendData?.reduce((sum, item) => sum + (item.value || 0), 0) || 0;
  // 根据 integerValue 选择格式化方式
  const totalFormatted = integerValue ? { value: keepZeroOrLine(total), unit: '' } : keepOneAndWan(total);

  return {
    color: ['#FF4D4F', '#FFCB47', '#2D8DFF', '#16DD8E', '#ABD335'],
    legend: {
      show: false,
    },
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: (params: any) => {
        const { data } = params;
        const valueFormatted = integerValue ? keepZeroOrLine(data?.value) : keepTwoOrLine(data?.value);
        return `
      <div>
        <div>${data?.name}：${valueFormatted}${unit || ''}</div>
        <div>占比：${keepTwoOrLine(data?.rate)}%</div>
      </div>
    `;
      },
    },
    graphic: {
      elements: [
        {
          type: 'image',
          style: {
            image: circle_spin,
            x: 60,
            y: 40,
            width: 90,
            height: 90,
          },
          keyframeAnimation: [
            {
              duration: 50000,
              loop: true,
              easing: 'linear',
              keyframes: [
                {
                  percent: 0,
                  rotation: 0,
                  transition: ['rotation'],
                },
                {
                  percent: 1,
                  rotation: 360,
                  transition: ['rotation'],
                },
              ],
            },
          ],
          origin: [105, 85],
          tooltip: {
            show: false,
          },
        },
      ],
    },
    series: [
      {
        type: 'pie',
        name: '总计',
        percentPrecision: 5,
        minAngle: 2,
        padAngle: 1,
        radius: [52, 60],
        left: 72,
        top: 52,
        width: 66,
        height: 66,
        label: centerLabel
          ? {
              show: true,
              position: 'center',
              formatter: () => `{value|${totalFormatted.value}${totalFormatted.unit || ''}}\n{label|${centerLabel}}`,
              rich: {
                value: {
                  fontSize: 20,
                  fontWeight: 500,
                  color: '#FFF',
                },
                label: {
                  fontSize: 12,
                  color: '#FFF',
                  padding: [4, 0, 0, 0],
                },
              },
            }
          : { show: false },
        data: pieLegendData,
        showEmptyCircle: false,
      },
      {
        showEmptyCircle: false,
        data: pieLegendData,
        type: 'pie',
        percentPrecision: 5,
        padAngle: 1,
        minAngle: 2,
        left: 72,
        top: 52,
        name: '总计',
        radius: [60, 76],
        width: 66,
        height: 66,
        emphasis: {
          disabled: true,
        },
        label: {
          show: false,
        },
        itemStyle: {
          opacity: 0.2,
        },
      },
    ],
  };
};

// 类型二：中间标题和值（无旋转背景）
export const getPieChartOptionWithTitle = (chartData?: PieChartData, titleText?: string) => {
  const totalFormatted = keepOneAndWan(chartData?.total);

  return {
    color: ['#FF6D2C', '#CE90D1', '#0DB6D9', '#63BC7F'],
    series: [
      {
        type: 'pie',
        percentPrecision: 5,
        minAngle: 2,
        padAngle: 1,
        radius: [66, 78],
        center: ['50%', '50%'],
        label: {
          show: true,
          position: 'center',
          formatter: () =>
            `{title|${titleText || '总量'}}\n{value|${totalFormatted.value}}\n{unit|${totalFormatted.unit}}`,
          rich: {
            title: {
              fontSize: 12,
              color: 'rgba(255,255,255,0.65)',
              padding: [0, 0, 4, 0],
            },
            value: {
              fontSize: 20,
              fontWeight: 500,
              color: '#FFF',
            },
            unit: {
              fontSize: 12,
              color: '#FFF',
              padding: [4, 0, 0, 0],
            },
          },
        },
        data: chartData?.items || [],
        showEmptyCircle: false,
      },
      {
        showEmptyCircle: false,
        data: chartData?.items || [],
        type: 'pie',
        percentPrecision: 5,
        padAngle: 1,
        minAngle: 2,
        radius: [78, 92],
        center: ['50%', '50%'],
        label: { show: false },
        itemStyle: { opacity: 0.2 },
      },
    ],
  };
};

// 类型三：不带旋转背景图（简单双层环形图）
export const getPieChartOptionSimple = (pieLegendData?: PieLegendItem[], unit?: string) => {
  return {
    color: ['#FF4D4F', '#FFCB47', '#2D8DFF', '#16DD8E', '#ABD335'],
    legend: {
      show: false,
    },
    tooltip: {
      trigger: 'item',
      confine: true,
      formatter: (params: any) => {
        const { data } = params;
        return `
      <div>
        <div>${data?.name}：${keepTwoOrLine(data?.value)}${unit || ''}</div>
        <div>占比：${keepTwoOrLine(data?.rate)}%</div>
      </div>
    `;
      },
    },
    series: [
      {
        type: 'pie',
        name: '总计',
        percentPrecision: 5,
        minAngle: 2,
        padAngle: 1,
        radius: [52, 60],
        center: ['50%', '50%'],
        label: {
          show: false,
        },
        data: pieLegendData,
        showEmptyCircle: false,
      },
      {
        showEmptyCircle: false,
        data: pieLegendData,
        type: 'pie',
        percentPrecision: 5,
        padAngle: 1,
        minAngle: 2,
        name: '总计',
        radius: [60, 76],
        center: ['50%', '50%'],
        emphasis: {
          disabled: true,
        },
        label: {
          show: false,
        },
        itemStyle: {
          opacity: 0.2,
        },
      },
    ],
  };
};

export interface PieLegendItem {
  name: string;
  value?: number;
  rate?: number;
  color: string;
}

export interface PieChartData {
  total?: number;
  items?: { name: string; value?: number }[];
}
